import Konva from "konva";
import { mmToPx, paperDimsMm } from "./paper.js";
import { drawSheetMarks } from "./sheet-marks.js";
import type { PageDef, PageRenderer } from "./types.js";

/**
 * Build an off-screen Konva.Stage that fits one {@link PageDef}.
 *
 * The stage is positioned/scaled so that `page.board` maps exactly to the
 * stage's `pixelW × pixelH` canvas. Callers append a single `<Layer>` and
 * draw their snapshot contents in *board coordinates* — the stage handles
 * the device-pixel scaling.
 *
 * The returned `destroy()` cleans up the Konva nodes **and** removes the
 * hidden host div so the renderer leaves no DOM footprint.
 */
export interface OffscreenStage {
  stage: Konva.Stage;
  pixelW: number;
  pixelH: number;
  destroy: () => void;
}

export function createOffscreenStage(page: PageDef, pixelRatio: number): OffscreenStage {
  const { wMm, hMm } = paperDimsMm(page.paper, page.orientation);
  const pixelW = mmToPx(wMm, pixelRatio);
  const pixelH = mmToPx(hMm, pixelRatio);

  // Host div — detached but in the DOM so Konva can measure text glyphs.
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-10000px";
  host.style.top = "-10000px";
  host.style.width = `${pixelW}px`;
  host.style.height = `${pixelH}px`;
  host.style.pointerEvents = "none";
  host.style.opacity = "0";
  document.body.appendChild(host);

  const stage = new Konva.Stage({
    container: host,
    width: pixelW,
    height: pixelH,
  });

  // Map page.board → centered inside (0,0,pixelW,pixelH) with **uniform**
  // scale, so the board keeps its native aspect ratio. The remaining space
  // on the long axis becomes letterbox/pillarbox padding (paper background).
  const scale = Math.min(pixelW / page.board.w, pixelH / page.board.h);
  const drawW = page.board.w * scale;
  const drawH = page.board.h * scale;
  const offsetX = (pixelW - drawW) / 2;
  const offsetY = (pixelH - drawH) / 2;
  stage.scaleX(scale);
  stage.scaleY(scale);
  stage.x(offsetX - page.board.x * scale);
  stage.y(offsetY - page.board.y * scale);

  return {
    stage,
    pixelW,
    pixelH,
    destroy: () => {
      stage.destroy();
      host.remove();
    },
  };
}

/**
 * Convenience renderer: builds an off-screen stage, hands its single layer
 * to the caller-provided `buildNodes` callback, then copies the stage's
 * canvas into the provided target canvas.
 *
 * This is the typical shape every surface-specific renderer wraps: the
 * surface only needs to know how to translate its snapshot into Konva
 * nodes inside one layer.
 */
export async function renderPageWithKonva<TSnap>(args: {
  snapshot: TSnap;
  page: PageDef;
  canvas: HTMLCanvasElement;
  pixelW: number;
  pixelH: number;
  buildNodes: (args: {
    snapshot: TSnap;
    page: PageDef;
    layer: Konva.Layer;
  }) => void | Promise<void>;
  /** Optional background draw — runs before `buildNodes`. */
  background?: string;
}): Promise<void> {
  // The offscreen stage was already sized by the caller (paper size at
  // pixel ratio), so we mirror that geometry here directly.
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-10000px";
  host.style.top = "-10000px";
  host.style.width = `${args.pixelW}px`;
  host.style.height = `${args.pixelH}px`;
  host.style.pointerEvents = "none";
  host.style.opacity = "0";
  document.body.appendChild(host);

  const stage = new Konva.Stage({
    container: host,
    width: args.pixelW,
    height: args.pixelH,
  });
  // Uniform scale → preserves the board's native aspect ratio on every paper
  // size. The remaining space on the long axis is filled by the page bg
  // (painted directly on the target canvas below), giving a clean letterbox.
  const scale = Math.min(args.pixelW / args.page.board.w, args.pixelH / args.page.board.h);
  const drawW = args.page.board.w * scale;
  const drawH = args.page.board.h * scale;
  const offsetX = (args.pixelW - drawW) / 2;
  const offsetY = (args.pixelH - drawH) / 2;
  stage.scaleX(scale);
  stage.scaleY(scale);
  stage.x(offsetX - args.page.board.x * scale);
  stage.y(offsetY - args.page.board.y * scale);

  try {
    const layer = new Konva.Layer();
    stage.add(layer);

    // Board-area background — covers the board rect itself (e.g. whiteboard
    // light/dark surface). The paper area outside the board is filled on the
    // target canvas below so letterbox bars are also tinted correctly.
    const bg = args.background ?? args.page.background;
    if (bg) {
      layer.add(
        new Konva.Rect({
          x: args.page.board.x,
          y: args.page.board.y,
          width: args.page.board.w,
          height: args.page.board.h,
          fill: bg,
          listening: false,
        }),
      );
    }

    await args.buildNodes({
      snapshot: args.snapshot,
      page: args.page,
      layer,
    });
    layer.draw();

    // Copy into the target canvas at the requested size.
    args.canvas.width = args.pixelW;
    args.canvas.height = args.pixelH;
    const ctx = args.canvas.getContext("2d");
    if (!ctx) throw new Error("Target canvas has no 2D context.");
    // Paper fill: covers the entire sheet, including letterbox bars left/right
    // or top/bottom of the (aspect-preserved) board. Defaults to white so PDF
    // prints look clean even when no explicit bg was set.
    const paperBg = bg ?? "#ffffff";
    ctx.fillStyle = paperBg;
    ctx.fillRect(0, 0, args.pixelW, args.pixelH);
    const src = stage.toCanvas({ pixelRatio: 1 });
    ctx.drawImage(src, 0, 0);

    // Assembly marks go on last, in paper space, so they sit at exact
    // millimetre positions regardless of how the board was scaled onto it.
    if (args.page.sheetMarks) {
      const sheet = paperDimsMm(args.page.paper, args.page.orientation);
      await drawSheetMarks({
        ctx,
        wMm: sheet.wMm,
        hMm: sheet.hMm,
        pixelW: args.pixelW,
        pixelH: args.pixelH,
        marks: args.page.sheetMarks,
      });
    }
  } finally {
    stage.destroy();
    host.remove();
  }
}

/**
 * Wrap a `buildNodes` callback into a {@link PageRenderer}.
 *
 * The returned renderer encapsulates the off-screen stage lifecycle so the
 * caller only has to think about painting Konva nodes for one page.
 */
export function makeKonvaRenderer<TSnap>(
  buildNodes: (args: {
    snapshot: TSnap;
    page: PageDef;
    layer: Konva.Layer;
  }) => void | Promise<void>,
): PageRenderer<TSnap> {
  return {
    draw: ({ snapshot, page, canvas, pixelW, pixelH }) =>
      renderPageWithKonva({
        snapshot,
        page,
        canvas,
        pixelW,
        pixelH,
        buildNodes,
      }),
  };
}
